import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Zap,
  Truck,
  Cable,
  Snowflake,
  CheckCircle2,
} from "lucide-react";
import Header from "./site/Header";
import Footer from "./site/Footer";
import Contact from "./site/Contact";
import { SITE } from "../site.config";
import { INDEXABLE_SUBURBS, SUBURB_PATH } from "../suburbs";
import useDocumentMeta from "../hooks/useDocumentMeta";

const ICONS = { Zap, Truck, Cable, Snowflake };

export default function SuburbPage({ suburb: suburbProp }) {
  const suburb = suburbProp || null;

  const safeName = suburb?.name || "Sunshine Coast";
  const safeSlug = suburb?.slug || "sunshine-coast";

  const title = `Auto Electrician ${safeName} | ${SITE.business.name}`;
  const description = `Trusted auto electrician servicing ${safeName} on the Sunshine Coast. 4WD fitouts, towing electrics, dual battery systems, auto air conditioning. Call ${SITE.business.phone}.`;
  const canonical = `https://powerzautoelectrical.com.au${SUBURB_PATH(safeSlug)}`;

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Auto Electrical, 4WD Accessories, Towing Electrics, Auto Air Conditioning",
    provider: {
      "@type": "AutoRepair",
      name: SITE.business.fullName,
      telephone: SITE.business.phoneTel,
      address: {
        "@type": "PostalAddress",
        streetAddress: SITE.business.address.line1,
        addressLocality: SITE.business.address.suburb,
        addressRegion: SITE.business.address.state,
        postalCode: SITE.business.address.postcode,
        addressCountry: SITE.business.address.country,
      },
    },
    areaServed: {
      "@type": "City",
      name: safeName,
      containedInPlace: { "@type": "AdministrativeArea", name: "Sunshine Coast, QLD" },
    },
    url: canonical,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://powerzautoelectrical.com.au/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: `Auto Electrician ${safeName}`,
        item: canonical,
      },
    ],
  };

  useDocumentMeta({
    title,
    description,
    canonical,
    image: SITE.business.logo,
    jsonLd: [serviceJsonLd, breadcrumbJsonLd],
  });

  if (!suburb) {
    return <Navigate to="/" replace />;
  }

  // Pick nearby suburbs for internal-link block — indexable pages only
  const others = INDEXABLE_SUBURBS.filter((s) => s.slug !== suburb.slug).slice(0, 8);

  return (
    <div className="bg-black text-white">
      <Header />

      {/* Hero */}
      <section
        data-testid={`suburb-hero-${suburb.slug}`}
        className="relative min-h-[80svh] w-full overflow-hidden bg-black pt-40 md:pt-48"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${SITE.hero.image})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 60%, #000 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-pz transition-colors bebas tracking-widest text-sm mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            ALL SERVICE AREAS
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm md:text-base">
              SUNSHINE COAST • {suburb.name.toUpperCase()}
            </span>
          </div>

          <h1
            data-testid="suburb-headline"
            className="bebas text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl"
          >
            AUTO ELECTRICIAN <br />
            <span className="text-cyan-pz glow-cyan">{suburb.name.toUpperCase()}</span>
          </h1>

          <p className="mt-8 text-white/80 barlow text-lg md:text-xl max-w-3xl leading-relaxed">
            {SITE.business.fullName} is your local auto electrical specialist
            servicing <strong className="text-white">{suburb.name}</strong> and
            surrounding {suburb.region} suburbs. From 4WD auto electrical fitouts and
            dual battery systems to towing electrics, brake controllers and auto
            air conditioning — we've got the gear and the experience to get it
            done right the first time.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${SITE.business.phoneTel}`}
              data-testid="suburb-call-btn"
              className="inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-white transition-colors clip-perf pulse-cyan"
            >
              <Phone className="w-5 h-5" />
              CALL {SITE.business.phone}
            </a>
            <a
              href={`mailto:${SITE.business.email}?subject=Powerz%20Auto%20Electrical%20-%20${suburb.name}%20Enquiry`}
              data-testid="suburb-quote-btn"
              className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-5 bebas text-xl tracking-[0.2em] hover:border-cyan-pz hover:text-cyan-pz transition-colors"
            >
              EMAIL US
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>

          <div className="mt-10 flex items-center gap-2 text-white/60 text-sm">
            <MapPin className="w-4 h-4 text-cyan-pz" />
            Workshop: {SITE.business.address.full} • Servicing {suburb.name}{" "}
            and all Sunshine Coast
          </div>
        </div>
      </section>

      {/* Services for this suburb */}
      <section className="relative bg-black py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              SERVING {suburb.name.toUpperCase()}
            </span>
          </div>
          <h2 className="bebas text-white text-4xl md:text-6xl leading-[0.9] mb-12 max-w-4xl">
            EVERYTHING WE DO FOR <br />
            <span className="text-cyan-pz">{suburb.name.toUpperCase()} LOCALS.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {SITE.services.map((s) => {
              const Icon = ICONS[s.icon] || Zap;
              return (
                <a
                  key={s.key}
                  href="#contact"
                  className="group relative block bg-[#0A0A0A] border border-white/10 hover:border-cyan-pz transition-all duration-300 overflow-hidden clip-perf"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={s.image}
                      alt={`${s.title} ${suburb.name}`}
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                    />
                  </div>
                  <div className="absolute top-5 left-5 bg-black/80 backdrop-blur-sm border border-cyan-pz/40 p-3">
                    <Icon className="w-6 h-6 text-cyan-pz" />
                  </div>
                  <div className="p-6 md:p-8">
                    <p className="bebas text-cyan-pz tracking-[0.2em] text-sm mb-2">
                      {s.kicker}
                    </p>
                    <h3 className="bebas text-white text-2xl md:text-3xl leading-tight mb-3">
                      {s.title} — {suburb.name}
                    </h3>
                    <p className="text-white/60 barlow leading-relaxed mb-5">
                      {s.desc}
                    </p>
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {s.bullets.map((b) => (
                        <li
                          key={b}
                          className="text-white/70 text-sm barlow flex items-start gap-2"
                        >
                          <CheckCircle2 className="w-4 h-4 text-cyan-pz mt-0.5 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other suburbs internal-link block */}
      <section className="relative bg-[#0A0A0A] py-20 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-pz" />
            <span className="bebas text-cyan-pz tracking-[0.3em] text-sm">
              OTHER LOCAL AREAS
            </span>
          </div>
          <h2 className="bebas text-white text-3xl md:text-5xl leading-[0.9] mb-10">
            ALSO SERVICING.
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={SUBURB_PATH(o.slug)}
                className="bg-black px-5 py-4 hover:bg-cyan-pz/10 transition-colors group"
              >
                <p className="bebas text-cyan-pz/60 tracking-widest text-xs">
                  AUTO ELECTRICIAN
                </p>
                <p className="bebas text-white tracking-wider text-lg group-hover:text-cyan-pz transition-colors">
                  {o.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
